// Added by Sejune Oh on 2020-06-09
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import axios from 'axios'

import { APSRule, APSEngine, APSCondition, APSScheduleItem } from '@/helper/define/apsDefine'
import { getQueryString } from '@/helper/util'
import { APSRuleElementProps } from '@/stores/mgmt/apsRule'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! CRUD

interface ReqCreateApsRule {
  fkDataStorageId: string
  name: string
  description: string
  apsEngine: string
  conditions: string
  apsEngineParam?: string
  studyStatusAfterCompletion?: string
  studyTagAfterCompletion?: string[]
  scheduleItems?: APSScheduleItem[],
  enable: boolean
}
/**
 * Create APS rule
 * @param fkDataStorageId: string
 * @param name: string
 * @param description: string
 * @param apsEngine: string
 * @param conditions: string
 * @param apsEngineParam?: string
 * @param studyStatusAfterCompletion?: string
 * @param studyTagAfterCompletion?: string[]
 * @param scheduleItems?: APSScheduleItem[]
 * @param enable: boolean
 */
export const reqCreateApsRule = async ({
  fkDataStorageId,
  name,
  description,
  apsEngine,
  conditions,
  apsEngineParam = '',
  studyStatusAfterCompletion,
  studyTagAfterCompletion = [],
  scheduleItems,
  enable
}: ReqCreateApsRule) => {
  return await axios.post('/v1/mgmt/aps/rules', {
    fk_data_storage_id: fkDataStorageId,
    name: name,
    description: description,
    aps_engine: apsEngine,
    conditions: conditions,
    aps_engine_param: apsEngineParam,
    study_status_after_completion: studyStatusAfterCompletion,
    study_tag_after_completion: studyTagAfterCompletion,
    schedule_items: scheduleItems,
    enable: enable
  })
}

interface ReqGetApsRules {
  filter?: string
  page_index?: number
  page_size?: number
  order_by?: string
  [key: string]: string | string[] | number | undefined
}
interface ResGetApsRules {
  total_count: string
  items: APSRule[]
}
/**
 * List APS rules
 * @param query
 */
export const reqGetApsRules = async (query: ReqGetApsRules) => {
  const queryStr = getQueryString<ReqGetApsRules>(query)
  return await axios.get<ResGetApsRules>(`v1/mgmt/aps/rules${queryStr}`)
}

/**
 * Get APS rule
 * @param id
 */
export const reqGetApsRule = async (id: string) => {
  return await axios.get<APSRule>(`v1/mgmt/aps/rules/${id}`)
  //
}

/**
 * Update APS rule
 * @param id
 * @param
 */
export const reqUpdateApsRule = async (
  id: string,
  {
    fkDataStorageId,
    name,
    description,
    apsEngine,
    conditions,
    apsEngineParam = '',
    studyStatusAfterCompletion,
    studyTagAfterCompletion = [],
    scheduleItems,
    enable
  }: ReqCreateApsRule) => {
  return await axios.put(`/v1/mgmt/aps/rules/${id}`, {
    fk_data_storage_id: fkDataStorageId,
    name: name,
    description: description,
    aps_engine: apsEngine,
    conditions: conditions,
    aps_engine_param: apsEngineParam,
    study_status_after_completion: studyStatusAfterCompletion,
    study_tag_after_completion: studyTagAfterCompletion,
    schedule_items: scheduleItems,
    enable: enable
  })
}

/**
 * Delete APS rule
 * @param id
 */
export const reqDeleteApsRule = async (id: string) => {
  return await axios.delete(`v1/mgmt/aps/rules/${id}`)
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! Get all APS rule conditions
interface ResGetAPSConditions {
  items: APSCondition[]
}
export const reqGetAPSConditions = async () => {
  return await axios.get<ResGetAPSConditions>('v1/mgmt/aps/conditions')
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! Get aps engines
interface ResGetAPSEngines {
  engines: APSEngine[]
}
export const reqGetAPSEngines = async () => {
  return await axios.get<ResGetAPSEngines>('v1/mgmt/aps/engines')
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! Get APS rule dependency by rule id
export const reqListApsRuleDependencies = async (id: string) => {
  return await axios.get<APSRuleElementProps>(`v1/mgmt/aps/rules/${id}/dependencies`)
}